# Route: /tracking/records/:recordId/amend
# Create an amendment to an existing entry
# Permission: TrackingRecord:amend

page-tracking-record-amend:
  type: Page
  archetype: edit
  meta:
    title: $t.tracking.amend.meta_title
    route: /tracking/records/:recordId/amend
    layout: shell
    auth: required
  # The record id reaches xp87-reason-field as its subject: a Widget's params resolve
  # route params via $params.*, not $route.*, which needs the parameter declared here.
  params:
    recordId:
      type: string
      required: true
  data:
    record:
      model: TrackingRecord
      key: $route.recordId
      required: true
    # Narrative pre-fill: amending means EDITING the record, not retyping it, so
    # the textarea seeds from the entry's current text (fetch-time reference seed,
    # exportRequest precedent — `form` MUST stay declared after `record`). The
    # structured fields are pre-filled the same way, inside xp87-tracking-amend-fields.
    form:
      model: AmendRecordForm
      initial:
        text: $data.record.text
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - amend-header
    - amend-original
    - amend-form

# ── Header ───────────────────────────────────────────────────────────────────

amend-header:
  type: PageHeader
  content: $t.tracking.amend.title
  back_route: /tracking/records/:recordId

# ── Original entry (read-only) ───────────────────────────────────────────────

amend-original:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.sm
  tokens:
    background: $color_scheme.light.color.surface-variant
    border: $border.default
    radius: $radius.md
  children:
    - amend-original-text

amend-original-text:
  type: Text
  bind:
    content: record.text
  tokens:
    font_size: $font.size.md
    color: $color_scheme.light.color.on-surface-muted

# ── Amendment form ───────────────────────────────────────────────────────────
#
# IMPORTANT: action type is `invoke`, NOT `create`. The `create` action would
# POST /api/tracking/records (i.e. silently create a brand-new entry with the
# amendment payload, leaving the original entry untouched — data corruption).
# `invoke` routes through providers.data.trackingEntry_amend(id, data), which
# hits POST /api/tracking/records/{id}/amend and creates an amendment chained
# to the original (declared in providers/data-provider.cir.yaml `operations:`).
#
# The Form has no card tokens. Fields are wrapped in an inner Section card so
# that sticky FormActions (on sm/md) sits outside the card border and does not
# visually overlap it.

amend-form:
  type: Form
  model: AmendRecordForm
  a11y:
    label: $t.tracking.amend.title
  action:
    type: invoke
    model: TrackingRecord
    operation: amend
    on_success: navigate(/tracking/records)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - amend-fields-card
    - amend-form-actions

amend-fields-card:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  # Content being justified comes first: re-file target, then the structured
  # template fields, then the corrected narrative (fields-before-narrative,
  # mirroring the detail order FR-4 and the create form). The reason-code control
  # sits last, immediately before the submit action (backlog
  # m1-reason-code-contextual-ux scope item 3 — a reason justifies what precedes it).
  children:
    - amend-retarget
    - amend-structured-fields
    - amend-field-text
    - amend-reason

# F80 FR-5 — optional re-file: amendment may target a different asset/tracking.
# The widget injects newContextType/newContextId/newTemplateName into the
# amend invoke payload; the server re-validates like a fresh create.
amend-retarget:
  type: Widget
  ref: xp87-tracking-amend-retarget

# FR-5 — structured template fields on the amend form. Thin wrapper around the
# canonical <xp87-tracking-fields-editor> (the create form hosts the same element):
# fetches the entry's PINNED template (ADR-181 form-as-it-was), renders the fields
# pre-filled from the entry's structuredData, and injects structuredData into the
# amend invoke payload (a capture-phase xp85-action interceptor, same pattern as
# amend-retarget and amend-reason). Node ids are amend-field-<name> (+ -error).
amend-structured-fields:
  type: Widget
  ref: xp87-tracking-amend-fields
  params:
    record: $data.record
    title: $t.tracking.amend.fields_title

amend-field-text:
  type: Input
  input_type: textarea
  label: $t.tracking.amend.new_text
  bind: form.text
  required: true
  a11y:
    label: $t.tracking.amend.new_text_label

# The reason, last — it justifies what precedes it. The ONE reason control, driven
# by the policy the server resolves for amending THIS record (its tracking form
# may declare a requirement of its own), so which codes are offered and whether
# free text is owed come from the server, never from this page. When the re-file
# section above moves the amendment to another tracking form, that form's
# declaration is the one the server applies, so the field follows the retarget
# widget's choice as its target form. The node prefix
# keeps the Tier 4/5 ids `amend-field-reason-code` / `-text` (+ `-error`).
amend-reason:
  type: Widget
  ref: xp87-reason-field
  params:
    transaction: TrackingRecord.amend
    subject-id: $params.recordId
    target-event: xp87-amend-retarget-changed
    target-key: formName
    action-model: TrackingRecord
    action-type: invoke
    action-operation: amend
    node-prefix: amend-field-reason

amend-form-actions:
  type: FormActions
  submit_label: $t.tracking.amend.btn_submit
  cancel_route: /tracking/records/:recordId
  cancel_params:
    recordId: $route.recordId
